import CompanyInteractions from '@/components/features/Companies/components/CompanyInteractions';
import AddMeetingModal from '@/components/features/Companies/forms/AddMeetingModal';
import AddPhoneCallModal from '@/components/features/Companies/forms/AddPhoneCallModal';
import AssignPersonToCompany from '@/components/features/Companies/forms/AssignPersonToCompany';
import AssignUserToCompany from '@/components/features/Companies/forms/AssignUserToCompany';
import CorrectCompanyIdentityModal from '@/components/features/Companies/forms/CorrectCompanyIdentityModal';
import EditCompany from '@/components/features/Companies/forms/EditCompany';
import MergeCompaniesModal from '@/components/features/Companies/forms/MergeCompaniesModal';
import { columns as leadsColumns } from '@/components/features/Leads/tables/Columns';
import { LeadCard } from '@/components/features/Leads/tables/LeadCard';
import { LeadTable } from '@/components/features/Leads/tables/LeadTable';
import { PersonCard } from '@/components/features/People/tables/PersonCard';
import { PersonTable } from '@/components/features/People/tables/PersonTable';
import { ProjectCard } from '@/components/features/Projects/tables/ProjectCard';
import { ProjectTable } from '@/components/features/Projects/tables/ProjectTable';
import { WorkOrderCard } from '@/components/features/WorkOrders/components/WorkOrderCard';
import { WorkOrderTable } from '@/components/features/WorkOrders/tables/WorkOrderTable';
import { AddressMapThumbnail } from '@/components/global/AddressMapThumbnail';
import { ConnectedActions, DeleteActionButton, DeleteConfirmAlert, DETAIL_ACTION_BUTTON_CLASS } from '@/components/global/detail-page-actions';
import { ResponsiveActions, type ResponsiveActionItem } from '@/components/global/ResponsiveActions';
import { UnifiedPrioritySelector } from '@/components/global/selectors/UnifiedPrioritySelector';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useSettings } from '@/contexts/SettingsContext';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile, useIsTabletOrMobile } from '@/hooks/use-mobile';
import { useNavigationVisibilityUi, type NavVisibilityKey } from '@/hooks/useNavigationVisibilityUi';
import { useResponsiveOverflow } from '@/hooks/useResponsiveOverflow';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { ProjectStatusOption } from '@/lib/constants-db';
import { getCountryName } from '@/lib/country-utils';
import { settingLabel } from '@/lib/status-labels';
import { cn, formatCurrency, formatDate, formatDateTime, useCan } from '@/lib/utils';
import {
    User as InterfaceUser,
    Lead,
    People,
    PriorityOption,
    Project,
    StatusOption,
    WorkOrder,
    WorkOrderAssignment,
    type BreadcrumbItem,
    type Company,
} from '@/types';
import { Head, Link, router, usePage } from '@inertiajs/react';
import {
    AlertCircle,
    BadgeCheck,
    BriefcaseBusiness,
    Building2,
    Calendar,
    CalendarDays,
    Clock,
    Edit,
    FolderOpen,
    Globe,
    Hash,
    Mail,
    MapPin,
    Merge,
    MoreHorizontal,
    Phone,
    Receipt,
    ShoppingCart,
    Tag,
    Trash2,
    UserPlus,
    Users,
    Wrench,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface ActivityItem {
    id: number;
    description: string;
    created_at: string;
    properties: Record<string, string | number | boolean | null | undefined>;
    causer?: InterfaceUser;
}

interface PhoneCall {
    id: string;
    description: string;
    call_date: string;
    call_time?: string | null;
    call_direction?: string | null;
    call_result?: string | null;
    duration_minutes?: number | null;
    created_at: string;
    created_by?: InterfaceUser;
    participants?: People[];
}

interface Meeting {
    id: string;
    description: string;
    meeting_date: string;
    meeting_time?: string;
    meeting_format?: string | null;
    location?: string | null;
    created_at: string;
    created_by?: InterfaceUser;
    participants?: People[];
}

interface CompanyPurchaseOrder {
    id: string;
    po_number: string;
    status: string;
    currency: string;
    order_date: string | null;
    expected_delivery_date: string | null;
    subtotal: number | string;
    directo_number: string | null;
    project: { id: string; contract_name: string; contract_number: string } | null;
    lead: { id: string; lead_number: string | null } | null;
    creator: { first_name: string; last_name: string } | null;
}

interface ShowCompanyPageProps {
    company: Company;
    people: People[];
    leads?: Lead[];
    projects?: Project[];
    workOrders?: WorkOrder[];
    purchaseOrders?: CompanyPurchaseOrder[];
    users: InterfaceUser[];
    phoneCalls?: PhoneCall[];
    meetings?: Meeting[];
    activities: ActivityItem[];
    [key: string]: unknown;
}

interface CompanyTab {
    value: string;
    label: string;
    displayLabel: ReactNode;
    count?: number;
    hiddenFromMainNav: boolean;
    content: ReactNode;
}

/** A labelled value in the detail cards. Falls back to a muted placeholder when empty. */
function DetailField({ label, icon, fallback, children }: { label: string; icon?: ReactNode; fallback?: string; children?: ReactNode }) {
    return (
        <div className="space-y-1">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
                {icon}
                {label}
            </div>
            <div className="text-sm font-medium break-words">{children ?? <span className="text-muted-foreground font-normal">{fallback}</span>}</div>
        </div>
    );
}

/** One segment of the headline metrics strip: coloured icon, label, value. */
function MetricTile({ icon, tone, label, children }: { icon: ReactNode; tone: string; label: string; children: ReactNode }) {
    return (
        <div className="flex items-center gap-3 p-4">
            <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
            <div className="min-w-0">
                <div className="text-muted-foreground text-xs font-medium">{label}</div>
                <div className="mt-0.5 truncate text-lg font-semibold">{children}</div>
            </div>
        </div>
    );
}

/** A compact tile inside the sidebar statistics card — subtle, never a card of its own. */
function StatTile({ icon, label, children, className }: { icon: ReactNode; label: string; children: ReactNode; className?: string }) {
    return (
        <div className={cn('bg-muted/40 rounded-lg border p-3', className)}>
            {/* The sidebar column is narrow, so long labels wrap rather than truncate. */}
            <div className="text-muted-foreground flex items-start gap-1.5 text-xs font-medium">
                <span className="mt-0.5 shrink-0">{icon}</span>
                <span className="min-w-0">{label}</span>
            </div>
            <div className="mt-1 text-sm font-semibold break-words">{children}</div>
        </div>
    );
}

/** One label/value row in the sidebar summary card. */
function SummaryRow({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
                <span className="shrink-0">{icon}</span>
                {label}
            </div>
            <div className="min-w-0 text-right text-sm font-medium break-words">{children}</div>
        </div>
    );
}

export default function ShowCompany() {
    const { props } = usePage<ShowCompanyPageProps>();
    const { company, people, leads, projects, workOrders, purchaseOrders: companyPurchaseOrders, phoneCalls, meetings } = props;
    const { t } = useTranslations();
    const { settings } = useSettings();
    const isMobile = useIsMobile();
    const isTabletOrMobile = useIsTabletOrMobile();
    const { shouldShowForNavKey, isAdminOnlyHiddenNavKey, tabDisplayLabel } = useNavigationVisibilityUi();
    const { can } = useCan();

    const companyUsers = company.users as InterfaceUser[];
    const companyPeople = people || [];
    const rawCompanyLeads = leads || [];
    const companyProjects = projects || [];
    const companyWorkOrders = workOrders || [];

    const companyLeads = useMemo(() => rawCompanyLeads, [rawCompanyLeads]);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('companies.index.breadcrumbs.companies', 'Companies'),
            href: '/companies',
        },
        {
            title: `${company.name}`,
            href: `/company/${company.id}/show`,
        },
    ];

    const fullAddress = useMemo(() => {
        const streetAddress = [company.address_street, company.address_house_number].filter(Boolean).join(' ').trim();
        const cityLine = [company.address_city, company.address_municipality, company.address_postal_code].filter(Boolean).join(', ').trim();
        const addressParts = [streetAddress, cityLine, company.country].filter(Boolean);
        if (addressParts.length > 0) {
            return addressParts.join(', ');
        }
        return company.address || '';
    }, [
        company.address,
        company.address_city,
        company.address_house_number,
        company.address_municipality,
        company.address_postal_code,
        company.address_street,
        company.country,
    ]);

    const hasAddressOrCoordinates = useMemo(
        () => Boolean(fullAddress.trim() || (company.address_lat != null && company.address_lng != null)),
        [company.address_lat, company.address_lng, fullAddress],
    );

    const notSpecified = t('common.not_specified', 'Not specified');

    const countryLabel = useMemo(
        () => (company.country ? getCountryName(company.country, settings.countries) || company.country : null),
        [company.country, settings.countries],
    );

    // Legacy rows can hold a non-array value in this json column.
    const categoryLabels = useMemo(
        () => (Array.isArray(company.category) ? company.category : company.category ? [String(company.category)] : []),
        [company.category],
    );

    const assignedUsers =
        companyUsers.length > 0
            ? companyUsers.map((user, index) => (
                  <span key={user.id}>
                      <Link href={`/employees/${user.id}`} className="text-primary hover:underline">
                          {user.first_name} {user.last_name}
                      </Link>
                      {index < companyUsers.length - 1 ? ', ' : ''}
                  </span>
              ))
            : null;

    const totalRevenue = useMemo(() => {
        const projectRevenue = companyProjects.reduce((total, project) => total + Number(project.sales_price || 0), 0);

        return projectRevenue;
    }, [companyProjects]);

    const latestActivity = useMemo(() => {
        const leadDates = companyLeads
            .map((lead) => lead.last_contacted)
            .filter((value): value is string => Boolean(value))
            .map((value) => new Date(value));
        const callDates = (phoneCalls || [])
            .map((call) => call.call_date || call.created_at)
            .filter((value): value is string => Boolean(value))
            .map((value) => new Date(value));
        const meetingDates = (meetings || [])
            .map((meeting) => meeting.meeting_date || meeting.created_at)
            .filter((value): value is string => Boolean(value))
            .map((value) => new Date(value));
        const allDates = [...leadDates, ...callDates, ...meetingDates];

        if (allDates.length === 0) {
            return null;
        }

        return allDates.reduce((latest, current) => (current > latest ? current : latest));
    }, [companyLeads, meetings, phoneCalls]);

    const [contactsViewMode, setContactsViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [contactsSearchValue, setContactsSearchValue] = useState('');
    const debouncedContactsSearch = useDebounce(contactsSearchValue, 350);
    const [contactsColumnFilterSearch, setContactsColumnFilterSearch] = useState<Record<string, string>>({});
    const [contactsSortColumn, setContactsSortColumn] = useState<string | null>(null);
    const [contactsSortDirection, setContactsSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [contactsVisibleColumns, setContactsVisibleColumns] = useState<Set<string>>(
        new Set(['full_name', 'position', 'email', 'phone', 'company', 'accounting']),
    );
    const [contactsColumnFilters, setContactsColumnFilters] = useState<Record<string, string[]>>({});
    const [contactsFilterOpen, setContactsFilterOpen] = useState(false);
    const [contactsSortOpen, setContactsSortOpen] = useState(false);
    const [contactsPage, setContactsPage] = useState(1);
    const [contactsPerPage, setContactsPerPage] = useState(10);
    const [leadsViewMode, setLeadsViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [leadsSearchValue, setLeadsSearchValue] = useState('');
    const debouncedLeadsSearch = useDebounce(leadsSearchValue, 350);
    const [leadsColumnFilterSearch, setLeadsColumnFilterSearch] = useState<Record<string, string>>({});
    const [leadsSortColumn, setLeadsSortColumn] = useState<string | null>(null);
    const [leadsSortDirection, setLeadsSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [leadsVisibleColumns, setLeadsVisibleColumns] = useState<Set<string>>(
        new Set(['lead_number', 'estimated_value', 'company', 'lead_owner', 'status', 'priority']),
    );
    const [leadsColumnFilters, setLeadsColumnFilters] = useState<Record<string, string[]>>({});
    const [leadsFilterOpen, setLeadsFilterOpen] = useState(false);
    const [leadsSortOpen, setLeadsSortOpen] = useState(false);
    const [leadsPage, setLeadsPage] = useState(1);
    const [leadsPerPage, setLeadsPerPage] = useState(10);
    const [projectsViewMode, setProjectsViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [projectsSearchValue, setProjectsSearchValue] = useState('');
    const debouncedProjectsSearch = useDebounce(projectsSearchValue, 350);
    const [projectsColumnFilterSearch, setProjectsColumnFilterSearch] = useState<Record<string, string>>({});
    const [projectsSortColumn, setProjectsSortColumn] = useState<string | null>(null);
    const [projectsSortDirection, setProjectsSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [projectsVisibleColumns, setProjectsVisibleColumns] = useState<Set<string>>(
        new Set(['name', 'contract_name', 'company', 'owner', 'status', 'start_date', 'address_street', 'sales_price']),
    );
    const [projectsColumnFilters, setProjectsColumnFilters] = useState<Record<string, string[]>>({});
    const [projectsFilterOpen, setProjectsFilterOpen] = useState(false);
    const [projectsSortOpen, setProjectsSortOpen] = useState(false);
    const [projectsPage, setProjectsPage] = useState(1);
    const [projectsPerPage, setProjectsPerPage] = useState(10);
    const [workOrdersViewMode, setWorkOrdersViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [workOrdersSearchValue, setWorkOrdersSearchValue] = useState('');
    const debouncedWorkOrdersSearch = useDebounce(workOrdersSearchValue, 350);
    const [workOrdersColumnFilterSearch, setWorkOrdersColumnFilterSearch] = useState<Record<string, string>>({});
    const [workOrdersSortColumn, setWorkOrdersSortColumn] = useState<string | null>(null);
    const [workOrdersSortDirection, setWorkOrdersSortDirection] = useState<'asc' | 'desc' | null>(null);
    const [workOrdersVisibleColumns, setWorkOrdersVisibleColumns] = useState<Set<string>>(
        new Set(['work_order', 'contact', 'service_address', 'company', 'assigned_to', 'type', 'status', 'priority', 'estimated_date']),
    );
    const [workOrdersColumnFilters, setWorkOrdersColumnFilters] = useState<Record<string, string[]>>({});
    const [workOrdersFilterOpen, setWorkOrdersFilterOpen] = useState(false);
    const [workOrdersSortOpen, setWorkOrdersSortOpen] = useState(false);
    const [workOrdersPage, setWorkOrdersPage] = useState(1);
    const [workOrdersPerPage, setWorkOrdersPerPage] = useState(10);

    const [poSearchValue, setPoSearchValue] = useState('');
    const [poStatusFilter, setPoStatusFilter] = useState<string | null>(null);
    const [poSortColumn, setPoSortColumn] = useState<string | null>(null);
    const [poSortDirection, setPoSortDirection] = useState<'asc' | 'desc' | null>(null);

    const contactsColumnDefinitions = useMemo(
        () => [
            { id: 'full_name', label: t('people.index.columns.full_name', 'Full Name') },
            { id: 'position', label: t('people.index.columns.position', 'Position') },
            { id: 'email', label: t('people.index.columns.email', 'Email') },
            { id: 'phone', label: t('people.index.columns.phone', 'Phone') },
            { id: 'company', label: t('people.index.columns.company', 'Company') },
            { id: 'accounting', label: t('people.index.columns.accounting', 'Accounting') },
        ],
        [t],
    );

    const contactsQuickFilterDefinitions = useMemo(
        () => [
            { id: 'company_id', label: t('people.index.quick_filters.company', 'Company') },
            { id: 'accounting', label: t('people.index.quick_filters.accounting', 'Accounting') },
        ],
        [t],
    );

    const contactsColumnFilterOptions = useMemo(() => {
        const optionSets: Record<string, Map<string, string>> = {};
        const addOption = (column: string, value?: string | null, label?: string | null) => {
            if (!value) {
                return;
            }
            optionSets[column] = optionSets[column] || new Map<string, string>();
            optionSets[column].set(value, label || value);
        };

        companyPeople.forEach((person) => {
            addOption('full_name', `${person.first_name} ${person.last_name}`.trim());
            addOption('position', person.position || null);
            addOption('email', person.email || null);
            addOption('phone', person.phone || null);
            if (person.company) {
                addOption('company_id', person.company.id, person.company.name);
            }
            addOption('accounting', person.accounting ? 'true' : 'false', person.accounting ? t('common.yes', 'Yes') : t('common.no', 'No'));
        });

        return {
            full_name: Array.from(optionSets.full_name?.entries() || []).map(([value, label]) => ({ value, label })),
            position: Array.from(optionSets.position?.entries() || []).map(([value, label]) => ({ value, label })),
            email: Array.from(optionSets.email?.entries() || []).map(([value, label]) => ({ value, label })),
            phone: Array.from(optionSets.phone?.entries() || []).map(([value, label]) => ({ value, label })),
            company_id: [{ value: company.id, label: company.name }],
            accounting: [
                { value: 'true', label: t('common.yes', 'Yes') },
                { value: 'false', label: t('common.no', 'No') },
            ],
        };
    }, [company.id, company.name, companyPeople, t]);

    const filteredContacts = useMemo(() => {
        let filtered = companyPeople.filter((person) => {
            const q = debouncedContactsSearch.trim().toLowerCase();
            const matchesSearch =
                q.length === 0 ||
                `${person.first_name} ${person.last_name}`.toLowerCase().includes(q) ||
                (person.email || '').toLowerCase().includes(q) ||
                (person.phone || '').toLowerCase().includes(q) ||
                (person.position || '').toLowerCase().includes(q);
            if (!matchesSearch) {
                return false;
            }

            return Object.entries(contactsColumnFilters).every(([column, values]) => {
                if (!values || values.length === 0) {
                    return true;
                }
                if (column === 'company_id') {
                    return values.includes(person.company?.id || '');
                }
                if (column === 'accounting') {
                    const boolValues = values.map((v) => v === 'true');
                    return boolValues.includes(Boolean(person.accounting));
                }
                if (column === 'full_name') {
                    return values.some((v) => `${person.first_name} ${person.last_name}`.toLowerCase().includes(v.toLowerCase()));
                }
                if (column === 'position') {
                    return values.some((v) => (person.position || '').toLowerCase().includes(v.toLowerCase()));
                }
                if (column === 'email') {
                    return values.some((v) => (person.email || '').toLowerCase().includes(v.toLowerCase()));
                }
                if (column === 'phone') {
                    return values.some((v) => (person.phone || '').toLowerCase().includes(v.toLowerCase()));
                }
                return true;
            });
        });

        if (contactsSortColumn && contactsSortDirection) {
            filtered = [...filtered].sort((a, b) => {
                const dir = contactsSortDirection === 'asc' ? 1 : -1;
                const as = (v: string | null | undefined) => (v || '').toLowerCase();
                if (contactsSortColumn === 'full_name') {
                    return as(`${a.first_name} ${a.last_name}`).localeCompare(as(`${b.first_name} ${b.last_name}`)) * dir;
                }
                if (contactsSortColumn === 'position') {
                    return as(a.position).localeCompare(as(b.position)) * dir;
                }
                if (contactsSortColumn === 'email') {
                    return as(a.email).localeCompare(as(b.email)) * dir;
                }
                if (contactsSortColumn === 'phone') {
                    return as(a.phone).localeCompare(as(b.phone)) * dir;
                }
                return 0;
            });
        }

        return filtered;
    }, [companyPeople, contactsColumnFilters, contactsSortColumn, contactsSortDirection, debouncedContactsSearch]);

    const contactsPagination = useMemo(() => {
        const total = filteredContacts.length;
        const lastPage = Math.max(1, Math.ceil(total / contactsPerPage));
        const currentPage = Math.min(contactsPage, lastPage);
        const from = total === 0 ? 0 : (currentPage - 1) * contactsPerPage + 1;
        const to = total === 0 ? 0 : Math.min(currentPage * contactsPerPage, total);
        const items = filteredContacts.slice(from - 1, to);
        return {
            items,
            meta: {
                current_page: currentPage,
                last_page: lastPage,
                per_page: contactsPerPage,
                total,
                from,
                to,
            },
        };
    }, [contactsPage, contactsPerPage, filteredContacts]);

    const contactsQuickFilterNames = contactsQuickFilterDefinitions.filter((q) => (contactsColumnFilters[q.id] || []).length > 0).map((q) => q.label);
    const contactsQuickFilterButtonLabel =
        contactsQuickFilterNames.length > 0
            ? `${t('people.index.toolbar.quick_filters', 'Quick filters')}: ${contactsQuickFilterNames.join(', ')}`
            : t('people.index.filters.all_people', 'All People');

    const handleContactsFilterChange = (column: string, values: string[]) => {
        setContactsPage(1);
        setContactsColumnFilters((prev) => {
            const next = { ...prev };
            if (!values.length) {
                delete next[column];
            } else {
                next[column] = values;
            }
            return next;
        });
    };

    const toggleContactsSingleFilter = (column: string, value: string) => {
        const current = contactsColumnFilters[column] || [];
        const exists = current.includes(value);
        const nextValues = exists ? current.filter((v) => v !== value) : [...current, value];
        handleContactsFilterChange(column, nextValues);
    };

    const clearContactsFilters = () => {
        setContactsColumnFilters({});
        setContactsSearchValue('');
        setContactsPage(1);
    };

    const handleContactsUpdate = {
        company: async (personId: string, companyId: string | null) => {
            router.put(`/person/${personId}`, { company_id: companyId || null }, { preserveState: true, preserveScroll: true });
        },
        position: async (personId: string, value: string | null) => {
            router.put(`/person/${personId}`, { position: value || null }, { preserveState: true, preserveScroll: true });
        },
        accounting: async (personId: string, value: boolean) => {
            router.put(`/person/${personId}`, { accounting: value }, { preserveState: true, preserveScroll: true });
        },
        email: async (personId: string, value: string | null) => {
            router.put(`/person/${personId}`, { email: value || null }, { preserveState: true, preserveScroll: true });
        },
        phone: async (personId: string, value: string | null) => {
            router.put(`/person/${personId}`, { phone: value || null }, { preserveState: true, preserveScroll: true });
        },
    };

    const leadsColumnDefinitions = useMemo(
        () => [
            { id: 'lead_number', label: t('leads.tables.lead_number', 'Lead Number') },
            { id: 'estimated_value', label: t('leads.tables.estimated_value', 'Estimated Value') },
            { id: 'company', label: t('leads.tables.company', 'Company') },
            { id: 'lead_owner', label: t('leads.tables.lead_owner', 'Lead Owner') },
            { id: 'status', label: t('leads.tables.status', 'Status') },
            { id: 'priority', label: t('leads.tables.priority', 'Priority') },
        ],
        [t],
    );

    const leadsQuickFilterDefinitions = useMemo(
        () => [
            { id: 'status', label: t('leads.index.quick_filters.status', 'Status') },
            { id: 'priority', label: t('leads.index.quick_filters.priority', 'Priority') },
        ],
        [t],
    );

    const leadsColumnFilterOptions = useMemo(() => {
        const ownerMap = new Map<string, string>();
        companyLeads.forEach((lead) => {
            const owner = lead.lead_owner || lead.assigned_user || lead.user;
            if (owner) {
                ownerMap.set(owner.id, `${owner.first_name || ''} ${owner.last_name || ''}`.trim());
            }
        });
        return {
            status: leadsColumns.length ? [] : [],
            priority: [],
            owner: Array.from(ownerMap.entries()).map(([value, label]) => ({ value, label })),
            company_id: [{ value: company.id, label: company.name }],
        } as Record<string, Array<{ value: string; label: string }>>;
    }, [company.id, company.name, companyLeads]);

    const filteredLeads = useMemo(() => {
        let filtered = companyLeads.filter((lead) => {
            const q = debouncedLeadsSearch.trim().toLowerCase();
            const owner = lead.lead_owner || lead.assigned_user || lead.user;
            const ownerName = owner ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim().toLowerCase() : '';
            const companyName = (lead.company?.name || '').toLowerCase();
            const matchesSearch =
                q.length === 0 ||
                (lead.lead_number || '').toLowerCase().includes(q) ||
                (lead.name || '').toLowerCase().includes(q) ||
                companyName.includes(q) ||
                ownerName.includes(q);
            if (!matchesSearch) {
                return false;
            }

            return Object.entries(leadsColumnFilters).every(([column, values]) => {
                if (!values || values.length === 0) {
                    return true;
                }
                if (column === 'status') {
                    return values.includes(String(lead.status));
                }
                if (column === 'priority') {
                    return values.includes(String(lead.priority));
                }
                if (column === 'owner') {
                    const owner = lead.lead_owner || lead.assigned_user || lead.user;
                    return owner ? values.includes(owner.id) : false;
                }
                if (column === 'company_id') {
                    return values.includes(lead.company?.id || '');
                }
                return true;
            });
        });

        if (leadsSortColumn && leadsSortDirection) {
            const dir = leadsSortDirection === 'asc' ? 1 : -1;
            filtered = [...filtered].sort((a, b) => {
                if (leadsSortColumn === 'lead_number') {
                    return String(a.lead_number || a.id).localeCompare(String(b.lead_number || b.id)) * dir;
                }
                if (leadsSortColumn === 'estimated_value') {
                    return ((a.estimated_value || 0) - (b.estimated_value || 0)) * dir;
                }
                if (leadsSortColumn === 'company') {
                    return String(a.company?.name || '').localeCompare(String(b.company?.name || '')) * dir;
                }
                if (leadsSortColumn === 'lead_owner') {
                    const ao = a.lead_owner || a.assigned_user || a.user;
                    const bo = b.lead_owner || b.assigned_user || b.user;
                    const an = ao ? `${ao.first_name || ''} ${ao.last_name || ''}` : '';
                    const bn = bo ? `${bo.first_name || ''} ${bo.last_name || ''}` : '';
                    return an.localeCompare(bn) * dir;
                }
                if (leadsSortColumn === 'status') {
                    return String(a.status).localeCompare(String(b.status)) * dir;
                }
                if (leadsSortColumn === 'priority') {
                    return String(a.priority).localeCompare(String(b.priority)) * dir;
                }
                return 0;
            });
        }
        return filtered;
    }, [companyLeads, debouncedLeadsSearch, leadsColumnFilters, leadsSortColumn, leadsSortDirection]);

    const leadsPagination = useMemo(() => {
        const total = filteredLeads.length;
        const lastPage = Math.max(1, Math.ceil(total / leadsPerPage));
        const currentPage = Math.min(leadsPage, lastPage);
        const from = total === 0 ? 0 : (currentPage - 1) * leadsPerPage + 1;
        const to = total === 0 ? 0 : Math.min(currentPage * leadsPerPage, total);
        const items = filteredLeads.slice(from - 1, to);
        return {
            items,
            meta: {
                current_page: currentPage,
                last_page: lastPage,
                per_page: leadsPerPage,
                total,
                from,
                to,
            },
        };
    }, [filteredLeads, leadsPage, leadsPerPage]);

    const leadsQuickFilterNames = leadsQuickFilterDefinitions.filter((q) => (leadsColumnFilters[q.id] || []).length > 0).map((q) => q.label);
    const leadsQuickFilterButtonLabel =
        leadsQuickFilterNames.length > 0
            ? `${t('leads.index.toolbar.quick_filters', 'Quick filters')}: ${leadsQuickFilterNames.join(', ')}`
            : t('leads.index.filters.all_leads', 'All Leads');
    const handleLeadsUpdate = {
        status: async (leadId: string, value: string) => {
            router.patch(
                `/leads/${leadId}/status`,
                { status: value },
                {
                    preserveState: true,
                    preserveScroll: true,
                },
            );
        },
        priority: async (leadId: string, value: string) => {
            router.patch(
                `/leads/${leadId}/priority`,
                { priority: value },
                {
                    preserveState: true,
                    preserveScroll: true,
                },
            );
        },
    };

    const projectsColumnDefinitions = useMemo(
        () => [
            { id: 'name', label: t('projects.tables.project', 'Project') },
            { id: 'contract_name', label: t('projects.tables.contract_name', 'Contract name') },
            { id: 'company', label: t('projects.tables.company', 'Company') },
            { id: 'owner', label: t('projects.tables.owner', 'Owner') },
            { id: 'status', label: t('projects.tables.status', 'Status') },
            { id: 'start_date', label: t('projects.tables.start_date', 'Start Date') },
            { id: 'address_street', label: t('projects.tables.address_street', 'Address Street') },
            { id: 'sales_price', label: t('projects.tables.sales_price', 'Sales Price') },
        ],
        [t],
    );

    const projectsQuickFilterDefinitions = useMemo(
        () => [
            { id: 'status', label: t('projects.index.quick_filters.status', 'Status') },
            { id: 'timeline_year', label: t('projects.index.filters.timeline_year', 'Project year') },
            { id: 'country_type', label: t('projects.tables.country_type', 'Country Type') },
            { id: 'company', label: t('projects.tables.company', 'Company') },
            { id: 'owner', label: t('projects.tables.owner', 'Owner') },
        ],
        [t],
    );

    const projectsColumnFilterOptions = useMemo(() => {
        const statusMap = new Map<string, string>();
        const ownerMap = new Map<string, string>();
        const yearMap = new Map<string, string>();
        const countryTypeMap = new Map<string, string>();
        companyProjects.forEach((project) => {
            if (project.status) {
                statusMap.set(project.status, project.status);
            }
            if (project.owner?.id) {
                ownerMap.set(project.owner.id, `${project.owner.first_name || ''} ${project.owner.last_name || ''}`.trim());
            }
            if (project.start_date) {
                const year = String(new Date(project.start_date).getFullYear());
                yearMap.set(year, year);
            }
            if (project.country_type) {
                countryTypeMap.set(String(project.country_type), String(project.country_type));
            }
        });

        return {
            status: Array.from(statusMap.entries()).map(([value, label]) => ({ value, label })),
            owner: Array.from(ownerMap.entries()).map(([value, label]) => ({ value, label })),
            company: [{ value: company.id, label: company.name }],
            timeline_year: Array.from(yearMap.entries()).map(([value, label]) => ({ value, label })),
            country_type: Array.from(countryTypeMap.entries()).map(([value, label]) => ({ value, label })),
        } as Record<string, Array<{ value: string; label: string }>>;
    }, [company.id, company.name, companyProjects]);

    const filteredProjects = useMemo(() => {
        let filtered = companyProjects.filter((project) => {
            const q = debouncedProjectsSearch.trim().toLowerCase();
            const matchesSearch =
                q.length === 0 ||
                String(project.contract_number || '')
                    .toLowerCase()
                    .includes(q) ||
                String(project.contract_name || '')
                    .toLowerCase()
                    .includes(q) ||
                String(project.company?.name || '')
                    .toLowerCase()
                    .includes(q);
            if (!matchesSearch) {
                return false;
            }

            return Object.entries(projectsColumnFilters).every(([column, values]) => {
                if (!values || values.length === 0) {
                    return true;
                }
                if (column === 'status') {
                    return values.includes(String(project.status));
                }
                if (column === 'owner') {
                    return project.owner?.id ? values.includes(project.owner.id) : false;
                }
                if (column === 'company') {
                    return values.includes(project.company?.id || '');
                }
                if (column === 'timeline_year') {
                    const year = project.start_date ? String(new Date(project.start_date).getFullYear()) : '';
                    return values.includes(year);
                }
                if (column === 'country_type') {
                    return values.includes(String(project.country_type || ''));
                }
                return true;
            });
        });

        if (projectsSortColumn && projectsSortDirection) {
            const dir = projectsSortDirection === 'asc' ? 1 : -1;
            filtered = [...filtered].sort((a, b) => {
                const str = (v: unknown) => String(v || '');
                if (projectsSortColumn === 'name') return str(a.contract_number).localeCompare(str(b.contract_number)) * dir;
                if (projectsSortColumn === 'contract_name') return str(a.contract_name).localeCompare(str(b.contract_name)) * dir;
                if (projectsSortColumn === 'company') return str(a.company?.name).localeCompare(str(b.company?.name)) * dir;
                if (projectsSortColumn === 'owner') {
                    const ao = `${a.owner?.first_name || ''} ${a.owner?.last_name || ''}`;
                    const bo = `${b.owner?.first_name || ''} ${b.owner?.last_name || ''}`;
                    return ao.localeCompare(bo) * dir;
                }
                if (projectsSortColumn === 'status') return str(a.status).localeCompare(str(b.status)) * dir;
                if (projectsSortColumn === 'start_date') return str(a.start_date).localeCompare(str(b.start_date)) * dir;
                if (projectsSortColumn === 'sales_price') return ((a.sales_price || 0) - (b.sales_price || 0)) * dir;
                return 0;
            });
        }
        return filtered;
    }, [companyProjects, debouncedProjectsSearch, projectsColumnFilters, projectsSortColumn, projectsSortDirection]);

    const projectsPagination = useMemo(() => {
        const total = filteredProjects.length;
        const lastPage = Math.max(1, Math.ceil(total / projectsPerPage));
        const currentPage = Math.min(projectsPage, lastPage);
        const from = total === 0 ? 0 : (currentPage - 1) * projectsPerPage + 1;
        const to = total === 0 ? 0 : Math.min(currentPage * projectsPerPage, total);
        const items = filteredProjects.slice(from - 1, to);
        return {
            items,
            meta: {
                current_page: currentPage,
                last_page: lastPage,
                per_page: projectsPerPage,
                total,
                from,
                to,
            },
        };
    }, [filteredProjects, projectsPage, projectsPerPage]);

    const projectsQuickFilterNames = projectsQuickFilterDefinitions.filter((q) => (projectsColumnFilters[q.id] || []).length > 0).map((q) => q.label);
    const projectsQuickFilterButtonLabel =
        projectsQuickFilterNames.length > 0
            ? `${t('projects.index.toolbar.quick_filters', 'Quick filters')}: ${projectsQuickFilterNames.join(', ')}`
            : t('projects.index.filters.all_projects', 'All Projects');
    const handleProjectsUpdate = {
        status: async (projectId: string, value: string) => {
            router.patch(
                `/projects/${projectId}/status`,
                { status: value },
                {
                    preserveState: true,
                    preserveScroll: true,
                },
            );
        },
    };
    const workOrdersColumnDefinitions = useMemo(
        () => [
            { id: 'work_order', label: t('work_orders.index.columns.work_order', 'Work Order') },
            { id: 'contact', label: t('work_orders.index.columns.contact', 'Contact') },
            { id: 'service_address', label: t('work_orders.index.columns.service_address', 'Service Address') },
            { id: 'company', label: t('work_orders.index.columns.company', 'Company') },
            { id: 'assigned_to', label: t('work_orders.index.columns.assigned_to', 'Assigned To') },
            { id: 'type', label: t('work_orders.index.columns.type', 'Type') },
            { id: 'status', label: t('work_orders.index.columns.status', 'Status') },
            { id: 'priority', label: t('work_orders.index.columns.priority', 'Priority') },
            { id: 'estimated_date', label: t('work_orders.index.columns.estimated_date', 'Estimated Date') },
        ],
        [t],
    );
    const workOrdersQuickFilterDefinitions = useMemo(
        () => [
            { id: 'status', label: t('work_orders.index.quick_filters.status', 'Status') },
            { id: 'type', label: t('work_orders.index.quick_filters.type', 'Type') },
            { id: 'priority', label: t('work_orders.index.quick_filters.priority', 'Priority') },
        ],
        [t],
    );
    const workOrdersColumnFilterOptions = useMemo(() => {
        const statusMap = new Map<string, string>();
        const priorityMap = new Map<string, string>();
        const typeMap = new Map<string, string>();
        const companyMap = new Map<string, string>();
        const assignedMap = new Map<string, string>();
        companyWorkOrders.forEach((wo) => {
            if (wo.status) statusMap.set(String(wo.status), String(wo.status));
            if (wo.priority) priorityMap.set(String(wo.priority), String(wo.priority));
            if (wo.type) typeMap.set(String(wo.type), String(wo.type));
            if (wo.company?.name) companyMap.set(String(wo.company?.id || wo.company.name), wo.company.name);
            const assignments = wo.assignments || [];
            assignments.forEach((a) => {
                if (a.user?.id) {
                    assignedMap.set(a.user.id, `${a.user.first_name || ''} ${a.user.last_name || ''}`.trim());
                }
            });
            if (wo.assigned_user?.id) {
                assignedMap.set(wo.assigned_user.id, `${wo.assigned_user.first_name || ''} ${wo.assigned_user.last_name || ''}`.trim());
            }
        });
        return {
            status: Array.from(statusMap.entries()).map(([value, label]) => ({ value, label })),
            priority: Array.from(priorityMap.entries()).map(([value, label]) => ({ value, label })),
            type: Array.from(typeMap.entries()).map(([value, label]) => ({ value, label })),
            company: Array.from(companyMap.entries()).map(([value, label]) => ({ value, label })),
            assigned_to: Array.from(assignedMap.entries()).map(([value, label]) => ({ value, label })),
        } as Record<string, Array<{ value: string; label: string }>>;
    }, [companyWorkOrders]);
    const normalizedWorkOrders = useMemo(
        () =>
            companyWorkOrders.map((wo) => {
                const hasAssignments = Array.isArray(wo.assignments) && wo.assignments.length > 0;
                const fallbackAssignments =
                    !hasAssignments && wo.assigned_user
                        ? [
                              {
                                  id: 'legacy-assignee',
                                  work_order_id: String(wo.id),
                                  user_id: wo.assigned_user.id,
                                  user: wo.assigned_user,
                                  role: 'lead' as const,
                                  assigned_at: '',
                                  assigned_by: undefined,
                                  created_at: '',
                                  updated_at: '',
                              },
                          ]
                        : [];

                const assignments: WorkOrderAssignment[] = (hasAssignments ? wo.assignments : fallbackAssignments) ?? [];
                const leadAssignment = assignments.find((assignment) => assignment.role === 'lead');

                return {
                    ...wo,
                    assignments,
                    assigned_user: wo.assigned_user || leadAssignment?.user || undefined,
                };
            }),
        [companyWorkOrders],
    );
    const filteredWorkOrders = useMemo(() => {
        let filtered = normalizedWorkOrders.filter((wo) => {
            const q = debouncedWorkOrdersSearch.trim().toLowerCase();
            const contactName = `${wo.contact_first_name || ''} ${wo.contact_last_name || ''}`.trim().toLowerCase();
            const matchesSearch =
                q.length === 0 ||
                String(wo.wo_number || wo.id || '')
                    .toLowerCase()
                    .includes(q) ||
                contactName.includes(q) ||
                String(wo.company?.name || '')
                    .toLowerCase()
                    .includes(q);
            if (!matchesSearch) return false;

            return Object.entries(workOrdersColumnFilters).every(([column, values]) => {
                if (!values?.length) return true;
                if (column === 'status') return values.includes(String(wo.status || ''));
                if (column === 'priority') return values.includes(String(wo.priority || ''));
                if (column === 'type') return values.includes(String(wo.type || ''));
                if (column === 'company') return values.includes(String(wo.company?.id || wo.company?.name || ''));
                if (column === 'assigned_to') {
                    const ids = [...(wo.assignments || []).map((a) => a.user?.id).filter(Boolean), wo.assigned_user?.id].filter((id): id is string =>
                        Boolean(id),
                    );
                    return ids.some((id) => values.includes(id));
                }
                return true;
            });
        });

        if (workOrdersSortColumn && workOrdersSortDirection) {
            const dir = workOrdersSortDirection === 'asc' ? 1 : -1;
            filtered = [...filtered].sort((a, b) => {
                if (workOrdersSortColumn === 'work_order') return String(a.wo_number || a.id).localeCompare(String(b.wo_number || b.id)) * dir;
                if (workOrdersSortColumn === 'contact')
                    return (
                        `${a.contact_first_name || ''} ${a.contact_last_name || ''}`.localeCompare(
                            `${b.contact_first_name || ''} ${b.contact_last_name || ''}`,
                        ) * dir
                    );
                if (workOrdersSortColumn === 'company') return String(a.company?.name || '').localeCompare(String(b.company?.name || '')) * dir;
                if (workOrdersSortColumn === 'status') return String(a.status || '').localeCompare(String(b.status || '')) * dir;
                if (workOrdersSortColumn === 'priority') return String(a.priority || '').localeCompare(String(b.priority || '')) * dir;
                if (workOrdersSortColumn === 'estimated_date')
                    return String(a.estimated_date || '').localeCompare(String(b.estimated_date || '')) * dir;
                return 0;
            });
        }
        return filtered;
    }, [normalizedWorkOrders, debouncedWorkOrdersSearch, workOrdersColumnFilters, workOrdersSortColumn, workOrdersSortDirection]);
    const workOrdersPagination = useMemo(() => {
        const total = filteredWorkOrders.length;
        const lastPage = Math.max(1, Math.ceil(total / workOrdersPerPage));
        const currentPage = Math.min(workOrdersPage, lastPage);
        const from = total === 0 ? 0 : (currentPage - 1) * workOrdersPerPage + 1;
        const to = total === 0 ? 0 : Math.min(currentPage * workOrdersPerPage, total);
        return {
            items: filteredWorkOrders.slice(from - 1, to),
            meta: { current_page: currentPage, last_page: lastPage, per_page: workOrdersPerPage, total, from, to },
        };
    }, [filteredWorkOrders, workOrdersPage, workOrdersPerPage]);
    const workOrdersQuickFilterNames = workOrdersQuickFilterDefinitions
        .filter((q) => (workOrdersColumnFilters[q.id] || []).length > 0)
        .map((q) => q.label);
    const workOrdersQuickFilterButtonLabel =
        workOrdersQuickFilterNames.length > 0
            ? `${t('work_orders.index.toolbar.quick_filters', 'Quick filters')}: ${workOrdersQuickFilterNames.join(', ')}`
            : t('work_orders.index.filters.all_work_orders', 'All Work Orders');
    const handleWorkOrdersUpdate = {
        estimatedDate: async (workOrderId: string, value: string | null) => {
            router.patch(`/work-orders/${workOrderId}`, { estimated_date: value || null }, { preserveState: true, preserveScroll: true });
        },
        priority: async (workOrderId: string, value: string) => {
            router.patch(`/work-orders/${workOrderId}/priority`, { priority: value }, { preserveState: true, preserveScroll: true });
        },
        status: async (workOrderId: string, value: string) => {
            router.patch(`/work-orders/${workOrderId}/status-editable`, { status: value }, { preserveState: true, preserveScroll: true });
        },
        type: async (workOrderId: string, value: string) => {
            router.patch(`/work-orders/${workOrderId}/details`, { work_type: value }, { preserveState: true, preserveScroll: true });
        },
        address: async (workOrderId: string, value: string, addressData?: { lat?: number; lng?: number }) => {
            router.patch(
                `/work-orders/${workOrderId}`,
                {
                    service_address_street: value,
                    service_address_lat: addressData?.lat,
                    service_address_lng: addressData?.lng,
                },
                { preserveState: true, preserveScroll: true },
            );
        },
        contact: async (
            workOrderId: string,
            data: {
                contact_first_name: string;
                contact_last_name: string;
                phone?: string;
                email?: string;
                service_address: string;
            },
        ) => {
            router.patch(
                `/work-orders/${workOrderId}/details`,
                {
                    contact_first_name: data.contact_first_name,
                    contact_last_name: data.contact_last_name,
                    phone: data.phone,
                    email: data.email,
                    service_address_street: data.service_address,
                },
                { preserveState: true, preserveScroll: true },
            );
        },
    };

    const tabs = useMemo(() => {
        type Row = {
            value: string;
            navKey?: NavVisibilityKey;
            label: string;
            count?: number;
            content: ReactNode;
        };

        const rows: Row[] = [
            {
                value: 'details',
                label: t('companies.show.tabs.details', 'Details'),
                content: (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:items-start">
                        {/* Main column — contact details and the activity log at ~75%. */}
                        <div className="space-y-4 lg:col-span-3">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle>{t('companies.show.sections.address_and_contacts', 'Address & contacts')}</CardTitle>
                                </CardHeader>
                                {/* Map on the left at a quarter of the card, fields alongside it. */}
                                <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-6">
                                    {/* Only worth the space when there is something to place on the map. */}
                                    {hasAddressOrCoordinates && (
                                        <AddressMapThumbnail
                                            address={fullAddress}
                                            lat={company.address_lat as number | null | undefined}
                                            lng={company.address_lng as number | null | undefined}
                                            heightClassName="h-40 md:h-full md:min-h-44"
                                            className="md:col-span-1"
                                        />
                                    )}
                                    {/* Three columns once there is room, so the fields fill the
                                        width beside the map instead of trailing off to the left. */}
                                    <div
                                        className={cn(
                                            'grid grid-cols-1 content-start gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3',
                                            hasAddressOrCoordinates ? 'md:col-span-3' : 'md:col-span-4',
                                        )}
                                    >
                                        <DetailField
                                            icon={<Globe className="h-4 w-4" />}
                                            label={t('companies.show.fields.country', 'Country')}
                                            fallback={notSpecified}
                                        >
                                            {countryLabel}
                                        </DetailField>
                                        <DetailField
                                            icon={<MapPin className="h-4 w-4" />}
                                            label={t('companies.show.fields.address', 'Address')}
                                            fallback={notSpecified}
                                        >
                                            {fullAddress || null}
                                        </DetailField>
                                        <DetailField
                                            icon={<Mail className="h-4 w-4" />}
                                            label={t('companies.show.fields.email', 'Email')}
                                            fallback={notSpecified}
                                        >
                                            {company.email ? (
                                                <a href={`mailto:${company.email}`} className="text-primary hover:underline">
                                                    {company.email}
                                                </a>
                                            ) : null}
                                        </DetailField>
                                        <DetailField
                                            icon={<Phone className="h-4 w-4" />}
                                            label={t('companies.show.fields.phone', 'Phone')}
                                            fallback={notSpecified}
                                        >
                                            {company.phone ? (
                                                <a href={`tel:${company.phone}`} className="text-primary hover:underline">
                                                    {company.phone}
                                                </a>
                                            ) : null}
                                        </DetailField>
                                        <DetailField
                                            icon={<Globe className="h-4 w-4" />}
                                            label={t('companies.show.fields.domain', 'Domain')}
                                            fallback={notSpecified}
                                        >
                                            {company.domain ? (
                                                <a
                                                    href={`https://${company.domain}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline"
                                                >
                                                    {company.domain}
                                                </a>
                                            ) : null}
                                        </DetailField>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* The activity log lives here rather than in its own tab — the
                                two-column layout leaves room for it beside the summary. */}
                            <CompanyInteractions
                                phoneCalls={phoneCalls || []}
                                meetings={meetings || []}
                                company={company}
                                people={companyPeople as People[]}
                            />
                        </div>

                        {/* Context column — orientation and supporting figures at ~25%. */}
                        <div className="space-y-4 lg:col-span-1">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle>{t('companies.show.sections.company_summary', 'Company summary')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-start gap-2.5">
                                        <span className="bg-primary/12 text-primary inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                                            <Building2 className="h-4 w-4" />
                                        </span>
                                        <div className="min-w-0 text-sm font-semibold break-words">{company.name}</div>
                                    </div>
                                    <div className="space-y-2.5 border-t pt-3">
                                        <SummaryRow
                                            icon={<BriefcaseBusiness className="h-3.5 w-3.5" />}
                                            label={t('companies.show.fields.type', 'Type')}
                                        >
                                            {company.type ? (
                                                settingLabel(t, 'company_types', company.type)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">{notSpecified}</span>
                                            )}
                                        </SummaryRow>
                                        {/* Priority stays inline-editable, as it was before the redesign. */}
                                        <SummaryRow icon={<Tag className="h-3.5 w-3.5" />} label={t('companies.show.fields.priority', 'Priority')}>
                                            <UnifiedPrioritySelector
                                                value={(company.priority as string) || 'medium'}
                                                priorityType="company"
                                                mode="editable"
                                                updateRoute="company.update-priority"
                                                resourceId={company.id}
                                            />
                                        </SummaryRow>
                                        <SummaryRow
                                            icon={<FolderOpen className="h-3.5 w-3.5" />}
                                            label={t('companies.show.fields.category', 'Category')}
                                        >
                                            {categoryLabels.length > 0 ? (
                                                categoryLabels.map((category) => settingLabel(t, 'company_categories', category)).join(', ')
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('companies.show.empty.no_categories', 'No categories specified')}
                                                </span>
                                            )}
                                        </SummaryRow>
                                        <SummaryRow icon={<Hash className="h-3.5 w-3.5" />} label={t('companies.show.fields.code', 'Code')}>
                                            {company.company_code || <span className="text-muted-foreground font-normal">{notSpecified}</span>}
                                        </SummaryRow>
                                        <SummaryRow icon={<Receipt className="h-3.5 w-3.5" />} label={t('companies.show.fields.vat', 'VAT')}>
                                            {company.vat || <span className="text-muted-foreground font-normal">{notSpecified}</span>}
                                        </SummaryRow>
                                        <SummaryRow icon={<Users className="h-3.5 w-3.5" />} label={t('companies.show.fields.assigned', 'Assigned')}>
                                            {assignedUsers ?? (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('companies.show.empty.no_users', 'No users assigned')}
                                                </span>
                                            )}
                                        </SummaryRow>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle>{t('companies.show.sections.statistics', 'Statistics')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <StatTile
                                            icon={<Clock className="h-3.5 w-3.5" />}
                                            label={t('companies.show.company.last_contacted', 'Last Contacted')}
                                        >
                                            {latestActivity ? (
                                                formatDateTime(latestActivity)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">
                                                    {t('companies.show.empty.no_contact_yet', 'No contact history')}
                                                </span>
                                            )}
                                        </StatTile>
                                        <StatTile
                                            icon={<Receipt className="h-3.5 w-3.5" />}
                                            label={t('companies.show.company.total_revenue', 'Total Revenue')}
                                        >
                                            {formatCurrency(totalRevenue)}
                                        </StatTile>
                                        <StatTile icon={<UserPlus className="h-3.5 w-3.5" />} label={t('companies.show.tabs.leads', 'Leads')}>
                                            {companyLeads.length}
                                        </StatTile>
                                        <StatTile icon={<FolderOpen className="h-3.5 w-3.5" />} label={t('companies.show.tabs.projects', 'Projects')}>
                                            {companyProjects.length}
                                        </StatTile>
                                        <StatTile
                                            icon={<Phone className="h-3.5 w-3.5" />}
                                            label={t('companies.show.company.phone_calls', 'Phone Calls')}
                                        >
                                            {(phoneCalls || []).length}
                                        </StatTile>
                                        {/* Six tiles fill three even rows; a full-width last tile
                                            left a hole beside the fifth. */}
                                        <StatTile
                                            icon={<CalendarDays className="h-3.5 w-3.5" />}
                                            label={t('companies.show.company.meetings', 'Meetings')}
                                        >
                                            {(meetings || []).length}
                                        </StatTile>
                                    </div>
                                    {/* Record metadata closes out the card — supporting, not headline. */}
                                    <div className="space-y-2.5 border-t pt-3">
                                        <SummaryRow icon={<Calendar className="h-3.5 w-3.5" />} label={t('companies.show.fields.created', 'Created')}>
                                            {company.created_at ? (
                                                formatDateTime(company.created_at)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">{notSpecified}</span>
                                            )}
                                        </SummaryRow>
                                        <SummaryRow icon={<Clock className="h-3.5 w-3.5" />} label={t('companies.show.fields.updated', 'Updated')}>
                                            {company.updated_at ? (
                                                formatDateTime(company.updated_at)
                                            ) : (
                                                <span className="text-muted-foreground font-normal">{notSpecified}</span>
                                            )}
                                        </SummaryRow>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                ),
            },
            {
                value: 'contacts',
                navKey: 'contacts',
                label: t('companies.show.tabs.contacts', 'Contacts'),
                count: companyPeople.length,
                content: (
                    <div className="space-y-4">
                        <GenericToolbar
                            quickFilterButtonLabel={contactsQuickFilterButtonLabel}
                            quickFilterDefinitions={contactsQuickFilterDefinitions}
                            columnDefinitions={contactsColumnDefinitions}
                            columnFilterOptions={contactsColumnFilterOptions}
                            columnFilters={contactsColumnFilters}
                            columnFilterSearch={contactsColumnFilterSearch}
                            setColumnFilterSearch={setContactsColumnFilterSearch}
                            toggleSingleFilter={toggleContactsSingleFilter}
                            handleFilterChange={handleContactsFilterChange}
                            clearFilters={clearContactsFilters}
                            isFilterOpen={contactsFilterOpen}
                            setIsFilterOpen={setContactsFilterOpen}
                            isSortOpen={contactsSortOpen}
                            setIsSortOpen={setContactsSortOpen}
                            sortColumn={contactsSortColumn}
                            sortDirection={contactsSortDirection}
                            handleSortChange={(column, direction) => {
                                setContactsSortColumn(column);
                                setContactsSortDirection(direction);
                            }}
                            clearSort={() => {
                                setContactsSortColumn(null);
                                setContactsSortDirection(null);
                            }}
                            search={contactsSearchValue}
                            setSearch={setContactsSearchValue}
                            perPage={contactsPerPage}
                            onPerPageChange={(perPage) => {
                                setContactsPerPage(perPage);
                                setContactsPage(1);
                            }}
                            viewMode={contactsViewMode}
                            setViewMode={setContactsViewMode}
                            visibleColumns={contactsVisibleColumns}
                            handleColumnShow={(columnId) =>
                                setContactsVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    next.add(columnId);
                                    return next;
                                })
                            }
                            handleColumnHide={(columnId) =>
                                setContactsVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    next.delete(columnId);
                                    return next;
                                })
                            }
                            filteredCount={contactsPagination.meta.total}
                            resourceName="people"
                            resourceNamePlural="people"
                            sortableColumns={[
                                { id: 'full_name', label: t('people.index.columns.full_name', 'Full Name') },
                                { id: 'position', label: t('people.index.columns.position', 'Position') },
                                { id: 'email', label: t('people.index.columns.email', 'Email') },
                            ]}
                            excludeFromMoreFilters={['company_id', 'accounting']}
                        />

                        {contactsViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {contactsPagination.items.map((person) => (
                                    <PersonCard key={person.id} person={person} />
                                ))}
                            </div>
                        ) : (
                            <PersonTable
                                people={contactsPagination.items}
                                companies={[{ id: company.id, name: company.name }]}
                                onUpdate={handleContactsUpdate}
                                sortColumn={contactsSortColumn}
                                sortDirection={contactsSortDirection}
                                onSortChange={(column, direction) => {
                                    setContactsSortColumn(column);
                                    setContactsSortDirection(direction);
                                }}
                                onColumnHide={(columnId) =>
                                    setContactsVisibleColumns((prev) => {
                                        const next = new Set(prev);
                                        next.delete(columnId);
                                        return next;
                                    })
                                }
                                onColumnShow={(columnId) =>
                                    setContactsVisibleColumns((prev) => {
                                        const next = new Set(prev);
                                        next.add(columnId);
                                        return next;
                                    })
                                }
                                visibleColumns={contactsVisibleColumns}
                                columnFilters={contactsColumnFilters}
                                columnFilterOptions={contactsColumnFilterOptions}
                                onFilterChange={handleContactsFilterChange}
                            />
                        )}

                        <PaginationControls
                            pagination={contactsPagination.meta}
                            onPageChange={setContactsPage}
                            onPerPageChange={(perPage) => {
                                setContactsPerPage(perPage);
                                setContactsPage(1);
                            }}
                            className="mt-2"
                        />

                        {contactsViewMode === 'grid' && contactsPagination.items.length === 0 && (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <p className="text-muted-foreground">
                                        {t('people.index.empty.no_people_found', 'No people found matching your filters.')}
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ),
            },
            {
                value: 'leads',
                navKey: 'leads',
                label: t('companies.show.tabs.leads', 'Leads'),
                count: companyLeads.length,
                content: (
                    <div className="space-y-4">
                        <GenericToolbar
                            quickFilterButtonLabel={leadsQuickFilterButtonLabel}
                            quickFilterDefinitions={leadsQuickFilterDefinitions}
                            columnDefinitions={leadsColumnDefinitions}
                            columnFilterOptions={leadsColumnFilterOptions}
                            columnFilters={leadsColumnFilters}
                            columnFilterSearch={leadsColumnFilterSearch}
                            setColumnFilterSearch={setLeadsColumnFilterSearch}
                            toggleSingleFilter={(column, value) => {
                                const current = leadsColumnFilters[column] || [];
                                const exists = current.includes(value);
                                const nextValues = exists ? current.filter((v) => v !== value) : [...current, value];
                                setLeadsPage(1);
                                setLeadsColumnFilters((prev) => ({ ...prev, [column]: nextValues }));
                            }}
                            handleFilterChange={(column, values) => {
                                setLeadsPage(1);
                                setLeadsColumnFilters((prev) => {
                                    const next = { ...prev };
                                    if (!values.length) {
                                        delete next[column];
                                    } else {
                                        next[column] = values;
                                    }
                                    return next;
                                });
                            }}
                            clearFilters={() => {
                                setLeadsColumnFilters({});
                                setLeadsSearchValue('');
                                setLeadsPage(1);
                            }}
                            isFilterOpen={leadsFilterOpen}
                            setIsFilterOpen={setLeadsFilterOpen}
                            isSortOpen={leadsSortOpen}
                            setIsSortOpen={setLeadsSortOpen}
                            sortColumn={leadsSortColumn}
                            sortDirection={leadsSortDirection}
                            handleSortChange={(column, direction) => {
                                setLeadsSortColumn(column);
                                setLeadsSortDirection(direction);
                            }}
                            clearSort={() => {
                                setLeadsSortColumn(null);
                                setLeadsSortDirection(null);
                            }}
                            search={leadsSearchValue}
                            setSearch={setLeadsSearchValue}
                            perPage={leadsPerPage}
                            onPerPageChange={(perPage) => {
                                setLeadsPerPage(perPage);
                                setLeadsPage(1);
                            }}
                            viewMode={leadsViewMode}
                            setViewMode={setLeadsViewMode}
                            visibleColumns={leadsVisibleColumns}
                            handleColumnShow={(columnId) =>
                                setLeadsVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    next.add(columnId);
                                    return next;
                                })
                            }
                            handleColumnHide={(columnId) =>
                                setLeadsVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    next.delete(columnId);
                                    return next;
                                })
                            }
                            filteredCount={leadsPagination.meta.total}
                            resourceName="leads"
                            resourceNamePlural="leads"
                            sortableColumns={[
                                { id: 'lead_number', label: t('leads.tables.lead_number', 'Lead Number') },
                                { id: 'estimated_value', label: t('leads.tables.estimated_value', 'Estimated Value') },
                                { id: 'company', label: t('leads.tables.company', 'Company') },
                                { id: 'lead_owner', label: t('leads.tables.lead_owner', 'Lead Owner') },
                                { id: 'status', label: t('leads.tables.status', 'Status') },
                                { id: 'priority', label: t('leads.tables.priority', 'Priority') },
                            ]}
                        />

                        {leadsViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {leadsPagination.items.map((lead) => (
                                    <LeadCard key={lead.id} lead={lead} />
                                ))}
                            </div>
                        ) : (
                            <LeadTable
                                leads={leadsPagination.items}
                                onUpdate={handleLeadsUpdate}
                                sortColumn={leadsSortColumn}
                                sortDirection={leadsSortDirection}
                                onSortChange={(column, direction) => {
                                    setLeadsSortColumn(column);
                                    setLeadsSortDirection(direction);
                                }}
                                onColumnHide={(columnId) =>
                                    setLeadsVisibleColumns((prev) => {
                                        const next = new Set(prev);
                                        next.delete(columnId);
                                        return next;
                                    })
                                }
                                onColumnShow={(columnId) =>
                                    setLeadsVisibleColumns((prev) => {
                                        const next = new Set(prev);
                                        next.add(columnId);
                                        return next;
                                    })
                                }
                                visibleColumns={leadsVisibleColumns}
                                columnFilters={leadsColumnFilters}
                                columnFilterOptions={leadsColumnFilterOptions}
                                onFilterChange={(column, values) => {
                                    setLeadsPage(1);
                                    setLeadsColumnFilters((prev) => {
                                        const next = { ...prev };
                                        if (!values.length) {
                                            delete next[column];
                                        } else {
                                            next[column] = values;
                                        }
                                        return next;
                                    });
                                }}
                                statuses={(settings.leadStatuses as unknown as StatusOption[]) || []}
                                priorities={(settings.leadPriorities as unknown as PriorityOption[]) || []}
                            />
                        )}

                        <PaginationControls
                            pagination={leadsPagination.meta}
                            onPageChange={setLeadsPage}
                            onPerPageChange={(perPage) => {
                                setLeadsPerPage(perPage);
                                setLeadsPage(1);
                            }}
                            className="mt-2"
                        />

                        {leadsViewMode === 'grid' && leadsPagination.items.length === 0 && (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <p className="text-muted-foreground">{t('leads.index.empty.no_leads_found', 'No leads found.')}</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ),
            },
            {
                value: 'projects',
                navKey: 'projects',
                label: t('companies.show.tabs.projects', 'Projects'),
                count: companyProjects.length,
                content: (
                    <div className="space-y-4">
                        <GenericToolbar
                            quickFilterButtonLabel={projectsQuickFilterButtonLabel}
                            quickFilterDefinitions={projectsQuickFilterDefinitions}
                            columnDefinitions={projectsColumnDefinitions}
                            columnFilterOptions={projectsColumnFilterOptions}
                            columnFilters={projectsColumnFilters}
                            columnFilterSearch={projectsColumnFilterSearch}
                            setColumnFilterSearch={setProjectsColumnFilterSearch}
                            toggleSingleFilter={(column, value) => {
                                const current = projectsColumnFilters[column] || [];
                                const exists = current.includes(value);
                                const nextValues = exists ? current.filter((v) => v !== value) : [...current, value];
                                setProjectsPage(1);
                                setProjectsColumnFilters((prev) => {
                                    const next = { ...prev };
                                    if (!nextValues.length) {
                                        delete next[column];
                                    } else {
                                        next[column] = nextValues;
                                    }
                                    return next;
                                });
                            }}
                            handleFilterChange={(column, values) => {
                                setProjectsPage(1);
                                setProjectsColumnFilters((prev) => {
                                    const next = { ...prev };
                                    if (!values.length) {
                                        delete next[column];
                                    } else {
                                        next[column] = values;
                                    }
                                    return next;
                                });
                            }}
                            clearFilters={() => {
                                setProjectsColumnFilters({});
                                setProjectsSearchValue('');
                                setProjectsPage(1);
                            }}
                            isFilterOpen={projectsFilterOpen}
                            setIsFilterOpen={setProjectsFilterOpen}
                            isSortOpen={projectsSortOpen}
                            setIsSortOpen={setProjectsSortOpen}
                            sortColumn={projectsSortColumn}
                            sortDirection={projectsSortDirection}
                            handleSortChange={(column, direction) => {
                                setProjectsSortColumn(column);
                                setProjectsSortDirection(direction);
                            }}
                            clearSort={() => {
                                setProjectsSortColumn(null);
                                setProjectsSortDirection(null);
                            }}
                            search={projectsSearchValue}
                            setSearch={setProjectsSearchValue}
                            perPage={projectsPagination.meta.per_page}
                            onPerPageChange={(perPage) => {
                                setProjectsPerPage(perPage);
                                setProjectsPage(1);
                            }}
                            viewMode={projectsViewMode}
                            setViewMode={setProjectsViewMode}
                            visibleColumns={projectsVisibleColumns}
                            handleColumnShow={(column) => setProjectsVisibleColumns((prev) => new Set([...prev, column]))}
                            handleColumnHide={(column) =>
                                setProjectsVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    next.delete(column);
                                    return next;
                                })
                            }
                            filteredCount={projectsPagination.meta.total}
                            resourceName="projects"
                            resourceNamePlural="projects"
                            sortableColumns={projectsColumnDefinitions}
                        />

                        {projectsViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {projectsPagination.items.map((project) => (
                                    <ProjectCard key={project.id} project={project} />
                                ))}
                            </div>
                        ) : (
                            <ProjectTable
                                projects={projectsPagination.items}
                                onUpdate={handleProjectsUpdate}
                                sortColumn={projectsSortColumn}
                                sortDirection={projectsSortDirection}
                                onSortChange={(column, direction) => {
                                    setProjectsSortColumn(column);
                                    setProjectsSortDirection(direction);
                                }}
                                onColumnHide={(column) =>
                                    setProjectsVisibleColumns((prev) => {
                                        const next = new Set(prev);
                                        next.delete(column);
                                        return next;
                                    })
                                }
                                onColumnShow={(column) => setProjectsVisibleColumns((prev) => new Set([...prev, column]))}
                                visibleColumns={projectsVisibleColumns}
                                columnFilters={projectsColumnFilters}
                                columnFilterOptions={projectsColumnFilterOptions}
                                onFilterChange={(column, values) => {
                                    setProjectsPage(1);
                                    setProjectsColumnFilters((prev) => {
                                        const next = { ...prev };
                                        if (!values.length) {
                                            delete next[column];
                                        } else {
                                            next[column] = values;
                                        }
                                        return next;
                                    });
                                }}
                                statuses={(settings.projectStatuses as unknown as ProjectStatusOption[]) || []}
                            />
                        )}

                        <PaginationControls
                            pagination={projectsPagination.meta}
                            onPageChange={setProjectsPage}
                            onPerPageChange={(perPage) => {
                                setProjectsPerPage(perPage);
                                setProjectsPage(1);
                            }}
                            className="mt-2"
                        />

                        {projectsViewMode === 'grid' && projectsPagination.items.length === 0 && (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <p className="text-muted-foreground">{t('projects.index.empty.no_projects_found', 'No projects found.')}</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ),
            },
            {
                value: 'work-orders',
                navKey: 'workOrders',
                label: t('companies.show.tabs.work_orders', 'Work Orders'),
                count: companyWorkOrders.length,
                content: (
                    <div className="space-y-4">
                        <GenericToolbar
                            quickFilterButtonLabel={workOrdersQuickFilterButtonLabel}
                            quickFilterDefinitions={workOrdersQuickFilterDefinitions}
                            columnDefinitions={workOrdersColumnDefinitions}
                            columnFilterOptions={workOrdersColumnFilterOptions}
                            columnFilters={workOrdersColumnFilters}
                            columnFilterSearch={workOrdersColumnFilterSearch}
                            setColumnFilterSearch={setWorkOrdersColumnFilterSearch}
                            toggleSingleFilter={(column, value) => {
                                const current = workOrdersColumnFilters[column] || [];
                                const exists = current.includes(value);
                                const nextValues = exists ? current.filter((v) => v !== value) : [...current, value];
                                setWorkOrdersPage(1);
                                setWorkOrdersColumnFilters((prev) => ({ ...prev, [column]: nextValues }));
                            }}
                            handleFilterChange={(column, values) => {
                                setWorkOrdersPage(1);
                                setWorkOrdersColumnFilters((prev) => ({ ...prev, [column]: values }));
                            }}
                            clearFilters={() => {
                                setWorkOrdersColumnFilters({});
                                setWorkOrdersSearchValue('');
                                setWorkOrdersPage(1);
                            }}
                            isFilterOpen={workOrdersFilterOpen}
                            setIsFilterOpen={setWorkOrdersFilterOpen}
                            isSortOpen={workOrdersSortOpen}
                            setIsSortOpen={setWorkOrdersSortOpen}
                            sortColumn={workOrdersSortColumn}
                            sortDirection={workOrdersSortDirection}
                            handleSortChange={(column, direction) => {
                                setWorkOrdersSortColumn(column);
                                setWorkOrdersSortDirection(direction);
                            }}
                            clearSort={() => {
                                setWorkOrdersSortColumn(null);
                                setWorkOrdersSortDirection(null);
                            }}
                            search={workOrdersSearchValue}
                            setSearch={setWorkOrdersSearchValue}
                            perPage={workOrdersPerPage}
                            onPerPageChange={(perPage) => {
                                setWorkOrdersPerPage(perPage);
                                setWorkOrdersPage(1);
                            }}
                            viewMode={workOrdersViewMode}
                            setViewMode={setWorkOrdersViewMode}
                            visibleColumns={workOrdersVisibleColumns}
                            handleColumnShow={(column) => setWorkOrdersVisibleColumns((prev) => new Set([...prev, column]))}
                            handleColumnHide={(column) =>
                                setWorkOrdersVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    next.delete(column);
                                    return next;
                                })
                            }
                            filteredCount={workOrdersPagination.meta.total}
                            resourceName="work_orders"
                            resourceNamePlural={t('work_orders.index.resource_plural', 'work orders')}
                            sortableColumns={workOrdersColumnDefinitions.filter((column) =>
                                ['work_order', 'contact', 'company', 'status', 'priority', 'estimated_date'].includes(column.id),
                            )}
                            excludeFromMoreFilters={['status', 'type', 'priority']}
                        />

                        {workOrdersViewMode === 'grid' ? (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {workOrdersPagination.items.map((workOrder) => (
                                    <WorkOrderCard
                                        key={workOrder.id}
                                        workOrder={workOrder}
                                        statuses={
                                            (settings.workOrderStatuses as Array<{
                                                value: string;
                                                label: string;
                                                color: string;
                                                isTerminal?: boolean;
                                            }>) || []
                                        }
                                        priorities={
                                            (settings.workOrderPriorities as Array<{ value: string; label: string; color: string; icon: string }>) ||
                                            []
                                        }
                                        types={
                                            (settings.workOrderTypes as Array<{ value: string; label: string; color: string; icon: string }>) || []
                                        }
                                    />
                                ))}
                            </div>
                        ) : (
                            <WorkOrderTable
                                workOrders={workOrdersPagination.items}
                                users={companyUsers}
                                onUpdate={handleWorkOrdersUpdate}
                                sortColumn={workOrdersSortColumn}
                                sortDirection={workOrdersSortDirection}
                                onSortChange={(column, direction) => {
                                    setWorkOrdersSortColumn(column);
                                    setWorkOrdersSortDirection(direction);
                                }}
                                onColumnHide={(column) =>
                                    setWorkOrdersVisibleColumns((prev) => {
                                        const next = new Set(prev);
                                        next.delete(column);
                                        return next;
                                    })
                                }
                                onColumnShow={(column) => setWorkOrdersVisibleColumns((prev) => new Set([...prev, column]))}
                                visibleColumns={workOrdersVisibleColumns}
                                columnFilters={workOrdersColumnFilters}
                                columnFilterOptions={workOrdersColumnFilterOptions}
                                onFilterChange={(column, values) => setWorkOrdersColumnFilters((prev) => ({ ...prev, [column]: values }))}
                                statuses={
                                    (settings.workOrderStatuses as Array<{ value: string; label: string; color: string; isTerminal?: boolean }>) || []
                                }
                                priorities={
                                    (settings.workOrderPriorities as Array<{ value: string; label: string; color: string; icon: string }>) || []
                                }
                                types={(settings.workOrderTypes as Array<{ value: string; label: string; color: string; icon: string }>) || []}
                            />
                        )}

                        <PaginationControls
                            pagination={workOrdersPagination.meta}
                            onPageChange={setWorkOrdersPage}
                            onPerPageChange={(perPage) => {
                                setWorkOrdersPerPage(perPage);
                                setWorkOrdersPage(1);
                            }}
                            className="mt-2"
                        />
                    </div>
                ),
            },
            {
                value: 'purchase-orders',
                navKey: 'purchaseOrders',
                label: t('purchase_orders.index.title', 'Purchase Orders'),
                count: (companyPurchaseOrders ?? []).length,
                content: (() => {
                    const allPos = companyPurchaseOrders ?? [];
                    const poStatusColors: Record<string, string> = {
                        draft: 'border-border bg-muted text-muted-foreground',
                        ordered: 'border-transparent bg-info-subtle text-info-subtle-foreground',
                        partially_received: 'border-transparent bg-warning-subtle text-warning-subtle-foreground',
                        received: 'border-transparent bg-success-subtle text-success-subtle-foreground',
                        cancelled: 'border-transparent bg-danger-subtle text-danger-subtle-foreground',
                    };
                    const poStatuses = ['draft', 'ordered', 'partially_received', 'received', 'cancelled'] as const;

                    const searchLower = poSearchValue.toLowerCase();
                    let filtered = allPos.filter((po) => {
                        if (poStatusFilter && po.status !== poStatusFilter) return false;
                        if (searchLower) {
                            return (
                                po.po_number.toLowerCase().includes(searchLower) ||
                                (po.project?.contract_name ?? '').toLowerCase().includes(searchLower) ||
                                (po.lead?.lead_number ?? '').toLowerCase().includes(searchLower)
                            );
                        }
                        return true;
                    });

                    if (poSortColumn && poSortDirection) {
                        filtered = [...filtered].sort((a, b) => {
                            let av: string | number = '';
                            let bv: string | number = '';
                            if (poSortColumn === 'po_number') {
                                av = a.po_number;
                                bv = b.po_number;
                            } else if (poSortColumn === 'status') {
                                av = a.status;
                                bv = b.status;
                            } else if (poSortColumn === 'order_date') {
                                av = a.order_date ?? '';
                                bv = b.order_date ?? '';
                            } else if (poSortColumn === 'expected_delivery') {
                                av = a.expected_delivery_date ?? '';
                                bv = b.expected_delivery_date ?? '';
                            } else if (poSortColumn === 'total') {
                                av = Number(a.subtotal ?? 0);
                                bv = Number(b.subtotal ?? 0);
                            }
                            const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
                            return poSortDirection === 'asc' ? cmp : -cmp;
                        });
                    }

                    const poTotal = allPos
                        .filter((po) => !['draft', 'cancelled'].includes(po.status))
                        .reduce((s, po) => s + Number(po.subtotal ?? 0), 0);

                    const toggleSort = (col: string) => {
                        if (poSortColumn === col) {
                            if (poSortDirection === 'asc') {
                                setPoSortDirection('desc');
                            } else {
                                setPoSortColumn(null);
                                setPoSortDirection(null);
                            }
                        } else {
                            setPoSortColumn(col);
                            setPoSortDirection('asc');
                        }
                    };

                    const sortIcon = (col: string) => (poSortColumn === col ? (poSortDirection === 'asc' ? ' ↑' : ' ↓') : '');

                    return (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPoStatusFilter(null)}
                                        className={cn(
                                            'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                                            poStatusFilter === null
                                                ? 'border-primary/40 bg-primary/10 text-primary'
                                                : 'border-border text-muted-foreground hover:bg-muted/50',
                                        )}
                                    >
                                        {t('common.all', 'All')}
                                    </button>
                                    {poStatuses.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setPoStatusFilter(poStatusFilter === s ? null : s)}
                                            className={cn(
                                                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                                                poStatusFilter === s ? poStatusColors[s] : 'border-border text-muted-foreground hover:bg-muted/50',
                                            )}
                                        >
                                            {t(`purchase_orders.statuses.${s}`, s.replace(/_/g, ' '))}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-3">
                                    {poTotal > 0 && (
                                        <span className="text-sm font-medium">
                                            {t('common.costs.total', 'Total')}:{' '}
                                            {new Intl.NumberFormat('lt-LT', { style: 'currency', currency: allPos[0]?.currency ?? 'EUR' }).format(
                                                poTotal,
                                            )}
                                        </span>
                                    )}
                                    <input
                                        type="text"
                                        value={poSearchValue}
                                        onChange={(e) => setPoSearchValue(e.target.value)}
                                        placeholder={t('purchase_orders.index.search_placeholder', 'Search POs...')}
                                        className="border-input bg-background focus:ring-ring h-8 w-48 rounded-md border px-3 text-sm outline-none focus:ring-1"
                                    />
                                </div>
                            </div>

                            <div className="bg-card overflow-hidden rounded-xl border">
                                <table className="w-full border-separate border-spacing-0 text-sm">
                                    <thead>
                                        <tr className="bg-muted/40 text-xs [&>th]:border-b">
                                            {[
                                                { id: 'po_number', label: t('purchase_orders.index.columns.po_number', 'PO Number') },
                                                {
                                                    id: 'project_lead',
                                                    label: t('purchase_orders.index.columns.project_lead', 'Project / Lead'),
                                                    noSort: true,
                                                },
                                                { id: 'status', label: t('purchase_orders.index.columns.status', 'Status') },
                                                { id: 'order_date', label: t('purchase_orders.index.columns.order_date', 'Order Date') },
                                                {
                                                    id: 'expected_delivery',
                                                    label: t('purchase_orders.index.columns.expected_delivery', 'Expected Delivery'),
                                                },
                                                { id: 'total', label: t('purchase_orders.index.columns.total', 'Total'), right: true },
                                            ].map((col) => (
                                                <th
                                                    key={col.id}
                                                    className={cn(
                                                        'text-muted-foreground px-4 py-2.5 font-medium',
                                                        col.right ? 'text-right' : 'text-left',
                                                        !col.noSort && 'hover:text-foreground cursor-pointer select-none',
                                                    )}
                                                    onClick={() => !col.noSort && toggleSort(col.id)}
                                                >
                                                    {col.label}
                                                    {!col.noSort && sortIcon(col.id)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {filtered.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-muted-foreground py-10 text-center">
                                                    {t('purchase_orders.index.no_results', 'No purchase orders found.')}
                                                </td>
                                            </tr>
                                        ) : (
                                            filtered.map((po) => {
                                                const isOverdue =
                                                    po.expected_delivery_date &&
                                                    !['received', 'cancelled'].includes(po.status) &&
                                                    new Date(po.expected_delivery_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
                                                return (
                                                    <tr
                                                        key={po.id}
                                                        className={cn(
                                                            'cursor-pointer transition-colors [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md',
                                                            isOverdue ? 'bg-destructive/5 hover:bg-destructive/10' : 'hover:bg-muted/50',
                                                        )}
                                                        onClick={() => router.visit(route('purchase-orders.show', po.id))}
                                                    >
                                                        <td className="px-4 py-2.5">
                                                            <Link
                                                                href={route('purchase-orders.show', po.id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-primary font-medium hover:underline"
                                                            >
                                                                {po.po_number}
                                                            </Link>
                                                        </td>
                                                        <td className="text-muted-foreground px-4 py-2.5">
                                                            {po.project?.contract_name ??
                                                                po.lead?.lead_number ??
                                                                t('purchase_orders.show.warehouse_stock', 'Warehouse / Stock')}
                                                        </td>
                                                        <td className="px-4 py-2.5">
                                                            <span
                                                                className={cn(
                                                                    'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
                                                                    poStatusColors[po.status] ?? '',
                                                                )}
                                                            >
                                                                {t(`purchase_orders.statuses.${po.status}`, po.status.replace(/_/g, ' '))}
                                                            </span>
                                                        </td>
                                                        <td className="text-muted-foreground px-4 py-2.5">{formatDate(po.order_date ?? '')}</td>
                                                        <td className={cn('px-4 py-2.5', isOverdue && 'text-destructive')}>
                                                            <div className="flex items-center gap-1">
                                                                {formatDate(po.expected_delivery_date ?? '')}
                                                                {isOverdue && <AlertCircle className="h-3.5 w-3.5" />}
                                                            </div>
                                                        </td>
                                                        <td
                                                            className={cn(
                                                                'px-4 py-2.5 text-right font-medium',
                                                                ['draft', 'cancelled'].includes(po.status) && 'text-muted-foreground line-through',
                                                            )}
                                                        >
                                                            {new Intl.NumberFormat('lt-LT', {
                                                                style: 'currency',
                                                                currency: po.currency ?? 'EUR',
                                                            }).format(Number(po.subtotal ?? 0))}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })(),
            },
        ];

        return rows
            .filter((row) => shouldShowForNavKey(row.navKey))
            .map(
                (row): CompanyTab => ({
                    value: row.value,
                    label: row.label,
                    count: row.count,
                    displayLabel: tabDisplayLabel(row.label, row.navKey, row.count),
                    hiddenFromMainNav: isAdminOnlyHiddenNavKey(row.navKey),
                    content: row.content,
                }),
            );
    }, [
        company,
        companyPeople,
        companyLeads,
        companyProjects,
        companyWorkOrders,
        companyUsers,
        contactsPagination,
        contactsQuickFilterButtonLabel,
        contactsQuickFilterDefinitions,
        contactsColumnDefinitions,
        contactsColumnFilterOptions,
        contactsColumnFilters,
        contactsColumnFilterSearch,
        contactsFilterOpen,
        contactsSortOpen,
        contactsSortColumn,
        contactsSortDirection,
        contactsSearchValue,
        contactsPerPage,
        contactsViewMode,
        contactsVisibleColumns,
        leadsColumnDefinitions,
        leadsColumnFilterOptions,
        leadsColumnFilterSearch,
        leadsColumnFilters,
        leadsFilterOpen,
        leadsPagination,
        leadsPerPage,
        leadsQuickFilterButtonLabel,
        leadsQuickFilterDefinitions,
        leadsSearchValue,
        leadsSortColumn,
        leadsSortDirection,
        leadsSortOpen,
        leadsViewMode,
        leadsVisibleColumns,
        projectsColumnDefinitions,
        projectsColumnFilterOptions,
        projectsColumnFilterSearch,
        projectsColumnFilters,
        projectsFilterOpen,
        projectsPagination,
        projectsPerPage,
        projectsQuickFilterButtonLabel,
        projectsQuickFilterDefinitions,
        projectsSearchValue,
        projectsSortColumn,
        projectsSortDirection,
        projectsSortOpen,
        projectsViewMode,
        projectsVisibleColumns,
        workOrdersColumnDefinitions,
        workOrdersColumnFilterOptions,
        workOrdersColumnFilterSearch,
        workOrdersColumnFilters,
        workOrdersFilterOpen,
        workOrdersPagination,
        workOrdersPerPage,
        workOrdersQuickFilterButtonLabel,
        workOrdersQuickFilterDefinitions,
        workOrdersSearchValue,
        workOrdersSortColumn,
        workOrdersSortDirection,
        workOrdersSortOpen,
        workOrdersViewMode,
        workOrdersVisibleColumns,
        notSpecified,
        countryLabel,
        categoryLabels,
        assignedUsers,
        latestActivity,
        meetings,
        phoneCalls,
        fullAddress,
        hasAddressOrCoordinates,
        totalRevenue,
        settings.workOrderPriorities,
        settings.workOrderStatuses,
        settings.workOrderTypes,
        settings.projectStatuses,
        shouldShowForNavKey,
        isAdminOnlyHiddenNavKey,
        tabDisplayLabel,
        companyPurchaseOrders,
        poSearchValue,
        poStatusFilter,
        poSortColumn,
        poSortDirection,
        t,
    ]);

    const defaultTab = useMemo(() => {
        if (tabs.some((tab) => tab.value === 'details')) {
            return 'details';
        }

        return tabs[0]?.value ?? 'details';
    }, [tabs]);

    const [activeTab, setActiveTab] = useState(defaultTab);
    const [mobileActionsDrawerOpen, setMobileActionsDrawerOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // The action modals used to be mounted three times over — once for the
    // desktop row, once for the mobile bar, once inside the actions drawer —
    // each with its own live form. They are now mounted once and driven from
    // here, which also lets the actions drawer finish closing before one of
    // them opens; two overlays at once leaves the page with a stuck
    // pointer-events lock.
    const [addMeetingOpen, setAddMeetingOpen] = useState(false);
    const [addPhoneCallOpen, setAddPhoneCallOpen] = useState(false);
    const [assignPersonOpen, setAssignPersonOpen] = useState(false);
    const [assignUserOpen, setAssignUserOpen] = useState(false);
    const [editCompanyOpen, setEditCompanyOpen] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [identityOpen, setIdentityOpen] = useState(false);

    type CompanyAction = 'meeting' | 'call' | 'contact' | 'employee' | 'edit' | 'merge' | 'identity' | 'delete';

    /**
     * Closes the actions drawer, then opens the chosen overlay once it has
     * animated out. The delay is not cosmetic: mounting the second while the
     * first is still leaving stacks two overlays and leaves the body with a
     * stuck pointer-events lock.
     *
     * The wait cannot hang off the drawer's onOpenChange — vaul only fires that
     * for closes it initiates itself, not for a controlled prop change.
     */
    const requestActionFromDrawer = (action: CompanyAction) => {
        setMobileActionsDrawerOpen(false);

        window.setTimeout(() => {
            if (action === 'meeting') setAddMeetingOpen(true);
            if (action === 'call') setAddPhoneCallOpen(true);
            if (action === 'contact') setAssignPersonOpen(true);
            if (action === 'employee') setAssignUserOpen(true);
            if (action === 'edit') setEditCompanyOpen(true);
            if (action === 'merge') setMergeOpen(true);
            if (action === 'identity') setIdentityOpen(true);
            if (action === 'delete') setDeleteConfirmOpen(true);
        }, 350);
    };

    const canDeleteCompany = can('companies.delete');
    const canMergeCompanies = can('settings.manage');

    const mainNavTabs = tabs.filter((tab) => !tab.hiddenFromMainNav);
    const hiddenNavTabs = tabs.filter((tab) => tab.hiddenFromMainNav);
    // Desktop: fit as many main tabs as the bar can hold, overflow the rest. Tablet/mobile uses its own scroll row.
    const {
        containerRef: tabsListRef,
        setItemRef: setTabRef,
        visibleCount: visibleTabCount,
    } = useResponsiveOverflow(
        mainNavTabs.map((tab) => tab.value),
        { gap: 4, reservedWidth: 104, widthBuffer: 8, enabled: !isTabletOrMobile },
    );
    const visibleTabs = isTabletOrMobile ? mainNavTabs : mainNavTabs.slice(0, visibleTabCount);
    const overflowTabs = isTabletOrMobile ? hiddenNavTabs : [...mainNavTabs.slice(visibleTabCount), ...hiddenNavTabs];
    const hasOverflow = overflowTabs.length > 0;
    const tabIcons: Record<string, ReactNode> = {
        details: <Building2 className="h-4 w-4" />,
        contacts: <Users className="h-4 w-4" />,
        leads: <Tag className="h-4 w-4" />,
        projects: <FolderOpen className="h-4 w-4" />,
        'work-orders': <Wrench className="h-4 w-4" />,
        'purchase-orders': <ShoppingCart className="h-4 w-4" />,
    };

    // One shared strip of headline figures, kept above the fold on every tab.
    const metricTiles = [
        <MetricTile
            key="leads"
            icon={<UserPlus className="h-5 w-5" />}
            tone="bg-cat-purple text-cat-purple-foreground"
            label={t('companies.show.tabs.leads', 'Leads')}
        >
            {companyLeads.length}
        </MetricTile>,
        <MetricTile
            key="projects"
            icon={<FolderOpen className="h-5 w-5" />}
            tone="bg-warning-subtle text-warning-subtle-foreground"
            label={t('companies.show.tabs.projects', 'Projects')}
        >
            {companyProjects.length}
        </MetricTile>,
        <MetricTile
            key="meetings"
            icon={<CalendarDays className="h-5 w-5" />}
            tone="bg-info-subtle text-info-subtle-foreground"
            label={t('companies.show.company.meetings', 'Meetings')}
        >
            {(meetings || []).length}
        </MetricTile>,
        <MetricTile
            key="revenue"
            icon={<Receipt className="h-5 w-5" />}
            tone="bg-success-subtle text-success-subtle-foreground"
            label={t('companies.show.company.total_revenue', 'Total Revenue')}
        >
            {formatCurrency(totalRevenue)}
        </MetricTile>,
    ];

    const actionItems: ResponsiveActionItem[] = [
        // Meeting and call are one segmented control: they are logged together and
        // must never be split across the row or into the overflow menu separately.
        {
            key: 'log-interaction',
            label: t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting'),
            icon: <CalendarDays className="h-4 w-4" />,
            // The pair shares one row slot, so it has to contribute both entries
            // to the overflow menu — otherwise the call action disappears.
            overflowActions: [
                {
                    key: 'add-meeting',
                    label: t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting'),
                    icon: <CalendarDays className="h-4 w-4" />,
                    onSelect: () => setAddMeetingOpen(true),
                },
                {
                    key: 'add-call',
                    label: t('companies.forms.add_phone_call.buttons.add_phone_call', 'Add Phone Call'),
                    icon: <Phone className="h-4 w-4" />,
                    onSelect: () => setAddPhoneCallOpen(true),
                },
            ],
            node: (
                <ConnectedActions>
                    <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={() => setAddMeetingOpen(true)}>
                        <CalendarDays className="h-4 w-4" />
                        {t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting')}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={DETAIL_ACTION_BUTTON_CLASS}
                        onClick={() => setAddPhoneCallOpen(true)}
                    >
                        <Phone className="h-4 w-4" />
                        {t('companies.forms.add_phone_call.buttons.add_phone_call', 'Add Phone Call')}
                    </Button>
                </ConnectedActions>
            ),
        },
        ...(can('assign-employees')
            ? [
                  {
                      key: 'assign-employee',
                      label: t('companies.forms.assign_user.buttons.assign_employee', 'Assign employee'),
                      icon: <Users className="h-4 w-4" />,
                      onOverflowSelect: () => setAssignUserOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={DETAIL_ACTION_BUTTON_CLASS}
                              onClick={() => setAssignUserOpen(true)}
                          >
                              <Users className="h-4 w-4" />
                              {t('companies.forms.assign_user.buttons.assign_employee', 'Assign employee')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        {
            key: 'contact',
            label: t('companies.forms.assign_person.buttons.add_contact', 'Add contact'),
            icon: <UserPlus className="h-4 w-4" />,
            overflowOnly: true,
            onOverflowSelect: () => setAssignPersonOpen(true),
            node: (
                <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={() => setAssignPersonOpen(true)}>
                    <UserPlus className="h-4 w-4" />
                    {t('companies.forms.assign_person.buttons.add_contact', 'Add contact')}
                </Button>
            ),
        },
        ...(canMergeCompanies
            ? [
                  {
                      key: 'merge',
                      label: t('companies.merge.action', 'Merge duplicates'),
                      icon: <Merge className="h-4 w-4" />,
                      onOverflowSelect: () => setMergeOpen(true),
                      node: (
                          <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={() => setMergeOpen(true)}>
                              <Merge className="h-4 w-4" />
                              {t('companies.merge.action', 'Merge duplicates')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
                  {
                      key: 'identity',
                      label: t('companies.identity.action', 'Correct identity'),
                      icon: <BadgeCheck className="h-4 w-4" />,
                      onOverflowSelect: () => setIdentityOpen(true),
                      node: (
                          <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={DETAIL_ACTION_BUTTON_CLASS}
                              onClick={() => setIdentityOpen(true)}
                          >
                              <BadgeCheck className="h-4 w-4" />
                              {t('companies.identity.action', 'Correct identity')}
                          </Button>
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
        {
            key: 'edit',
            label: t('common.edit', 'Edit'),
            icon: <Edit className="h-4 w-4" />,
            pinned: true,
            onOverflowSelect: () => setEditCompanyOpen(true),
            node: (
                <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={() => setEditCompanyOpen(true)}>
                    <Edit className="h-4 w-4" />
                    {t('common.edit', 'Edit')}
                </Button>
            ),
        },
        ...(canDeleteCompany
            ? [
                  {
                      key: 'delete',
                      label: t('companies.show.actions.delete', 'Delete Company'),
                      icon: <Trash2 className="h-4 w-4" />,
                      destructive: true,
                      pinned: true,
                      onOverflowSelect: () => setDeleteConfirmOpen(true),
                      node: (
                          <DeleteActionButton
                              label={t('companies.show.actions.delete', 'Delete Company')}
                              onClick={() => setDeleteConfirmOpen(true)}
                          />
                      ),
                  } satisfies ResponsiveActionItem,
              ]
            : []),
    ];

    return (
        <>
            <Head title={t('companies.show.title', 'Company Details')} />

            <ShowLayout breadcrumbs={breadcrumbs} title={company.name}>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 w-full flex-1 flex-col">
                    <div className="mb-4 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
                        <div ref={tabsListRef} className="min-w-0 lg:flex-[3]">
                            {isTabletOrMobile ? (
                                <div className="-mx-1 overflow-x-auto pb-1">
                                    <div className="flex w-max min-w-full items-stretch gap-2 px-1">
                                        {[...visibleTabs, ...overflowTabs].map((tab) => {
                                            const isActive = activeTab === tab.value;
                                            return (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className={cn(
                                                        'h-auto min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors',
                                                        isActive
                                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                                            : 'border-border bg-background text-foreground',
                                                    )}
                                                >
                                                    <span className="flex flex-col items-start gap-1.5">
                                                        <span className="text-muted-foreground">{tabIcons[tab.value]}</span>
                                                        <span className="text-sm leading-tight font-medium">{tab.displayLabel}</span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <TabsList
                                    className={cn(
                                        'bg-muted inline-flex h-auto w-fit max-w-full min-w-0 flex-nowrap items-center gap-1 rounded-xl px-1 py-1',
                                    )}
                                >
                                    {visibleTabs.map((tab) => (
                                        <TabsTrigger
                                            key={tab.value}
                                            ref={setTabRef(tab.value)}
                                            value={tab.value}
                                            className={cn('shrink-0 rounded-md text-sm')}
                                        >
                                            <span className="inline-flex items-center gap-2 whitespace-nowrap">
                                                {tabIcons[tab.value]}
                                                {tab.displayLabel}
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                    {hasOverflow && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'focus-visible:ring-ring inline-flex max-w-[180px] shrink-0 items-center justify-center truncate rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                                                        overflowTabs.find((x) => x.value === activeTab)
                                                            ? 'bg-secondary text-secondary-foreground'
                                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                                    )}
                                                >
                                                    {overflowTabs.find((x) => x.value === activeTab)
                                                        ? overflowTabs.find((x) => x.value === activeTab)?.displayLabel
                                                        : t('work_orders.show.tabs.more', '+{count} more').replace(
                                                              '{count}',
                                                              String(overflowTabs.length),
                                                          )}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[150px]">
                                                {overflowTabs.map((tab) => (
                                                    <DropdownMenuItem
                                                        key={tab.value}
                                                        onClick={() => setActiveTab(tab.value)}
                                                        className={cn(activeTab === tab.value && 'bg-accent font-medium')}
                                                    >
                                                        <span className="inline-flex items-center gap-2">
                                                            {tabIcons[tab.value]}
                                                            {tab.displayLabel}
                                                        </span>
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </TabsList>
                            )}
                        </div>

                        <div className="hidden min-w-0 lg:flex lg:flex-[2]">
                            <ResponsiveActions className="w-full" items={actionItems} />
                        </div>
                    </div>

                    {/* Headline metrics stay above the fold on every tab. */}
                    <div className="bg-card mb-4 grid grid-cols-1 divide-y overflow-hidden rounded-xl border sm:grid-cols-2 sm:divide-x xl:grid-cols-5 xl:divide-y-0">
                        {metricTiles}
                    </div>

                    {tabs.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value} className="mt-0 outline-none data-[state=inactive]:hidden">
                            {tab.content}
                        </TabsContent>
                    ))}
                </Tabs>
            </ShowLayout>

            {isTabletOrMobile && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur lg:hidden">
                    <div className="grid grid-cols-6 gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setAddMeetingOpen(true)}
                            aria-label={t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting')}
                        >
                            <CalendarDays className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setAddPhoneCallOpen(true)}
                            aria-label={t('companies.forms.add_phone_call.buttons.add_phone_call', 'Add Phone Call')}
                        >
                            <Phone className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setAssignPersonOpen(true)}
                            aria-label={t('companies.forms.assign_person.buttons.add_contact', 'Add contact')}
                        >
                            <UserPlus className="h-4 w-4" />
                        </Button>
                        {can('assign-employees') && (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setAssignUserOpen(true)}
                                aria-label={t('companies.forms.assign_user.buttons.assign_employee', 'Assign employee')}
                            >
                                <Users className="h-4 w-4" />
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setEditCompanyOpen(true)}
                            aria-label={t('companies.forms.edit.buttons.edit_company', 'Edit company')}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setMobileActionsDrawerOpen(true)}
                            aria-label={t('common.more_actions', 'More actions')}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            <Drawer open={mobileActionsDrawerOpen} onOpenChange={setMobileActionsDrawerOpen} direction="bottom">
                <DrawerContent className="lg:hidden">
                    <DrawerHeader>
                        <DrawerTitle>{t('companies.sidebar.actions', 'Actions')}</DrawerTitle>
                        <DrawerDescription>{t('common.select_action', 'Select an action')}</DrawerDescription>
                    </DrawerHeader>
                    <div className="space-y-1 p-4 pt-0">
                        <Button
                            type="button"
                            variant="ghost"
                            className="h-11 w-full justify-start gap-2"
                            onClick={() => requestActionFromDrawer('meeting')}
                        >
                            <CalendarDays className="h-4 w-4" />
                            {t('companies.forms.add_meeting.buttons.add_meeting', 'Add Meeting')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            className="h-11 w-full justify-start gap-2"
                            onClick={() => requestActionFromDrawer('call')}
                        >
                            <Phone className="h-4 w-4" />
                            {t('companies.forms.add_phone_call.buttons.add_phone_call', 'Add Phone Call')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            className="h-11 w-full justify-start gap-2"
                            onClick={() => requestActionFromDrawer('contact')}
                        >
                            <UserPlus className="h-4 w-4" />
                            {t('companies.forms.assign_person.buttons.add_contact', 'Add contact')}
                        </Button>
                        {can('assign-employees') && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('employee')}
                            >
                                <Users className="h-4 w-4" />
                                {t('companies.forms.assign_user.buttons.assign_employee', 'Assign employee')}
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            className="h-11 w-full justify-start gap-2"
                            onClick={() => requestActionFromDrawer('edit')}
                        >
                            <Edit className="h-4 w-4" />
                            {t('common.edit', 'Edit')}
                        </Button>
                        {canDeleteCompany && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-destructive hover:text-destructive h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('delete')}
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('companies.show.actions.delete', 'Delete Company')}
                            </Button>
                        )}
                    </div>
                </DrawerContent>
            </Drawer>
            {isTabletOrMobile && <div className="h-24 lg:h-0" />}

            {/* Mounted once, driven by the desktop row, the mobile bar and the
                actions drawer alike. */}
            <AddMeetingModal company={company} people={companyPeople} open={addMeetingOpen} onOpenChange={setAddMeetingOpen} />
            <AddPhoneCallModal company={company} users={companyPeople} open={addPhoneCallOpen} onOpenChange={setAddPhoneCallOpen} />
            <AssignPersonToCompany
                company={company}
                people={[]}
                companyPeople={companyPeople}
                open={assignPersonOpen}
                onOpenChange={setAssignPersonOpen}
            />
            {can('assign-employees') && (
                <AssignUserToCompany companyId={company.id} companyUsers={companyUsers} open={assignUserOpen} onOpenChange={setAssignUserOpen} />
            )}
            <EditCompany company={company} open={editCompanyOpen} onOpenChange={setEditCompanyOpen} />
            {canMergeCompanies && (
                <>
                    <MergeCompaniesModal
                        companies={[{ id: company.id, name: company.name, company_code: company.company_code, country: company.country }]}
                        open={mergeOpen}
                        onOpenChange={setMergeOpen}
                    />
                    <CorrectCompanyIdentityModal
                        company={{ id: company.id, name: company.name, company_code: company.company_code }}
                        open={identityOpen}
                        onOpenChange={setIdentityOpen}
                    />
                </>
            )}

            <DeleteConfirmAlert
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                title={t('companies.show.delete.confirm_title', 'Delete Company')}
                description={t(
                    'companies.show.delete.confirm_description',
                    'Are you sure you want to delete ":name"? This action cannot be undone.',
                ).replace(':name', company.name || company.id)}
                cancelLabel={t('companies.show.delete.cancel', 'Cancel')}
                confirmLabel={t('companies.show.delete.confirm_button', 'Delete')}
                onConfirm={() => {
                    setDeleteConfirmOpen(false);
                    router.delete(route('companies.destroy', company.id), {
                        onSuccess: () => toast.success(t('companies.show.delete.success', 'Company deleted.')),
                        onError: () => toast.error(t('companies.show.delete.failed', 'Failed to delete company.')),
                    });
                }}
            />
        </>
    );
}
